import {Component} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WorkflowService} from "../workflow.service";
import {WorkflowStep} from "../workflow-step";
import {WorkflowTargetInfoModel} from "../models/workflow-target-info.model";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {CustomerService} from "../../customer/customer-service";
import {ProjectService} from "../../project/project.service";
import {CustomerModel} from "../../customer/customer.model";
import {ConfirmDialogService} from "../../confirm-dialog/confirm-dialog-service";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {CustomerCustomHeadersDialogComponent} from "./select-target-customer-custom-header-edit-dialog.component";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {toggleLoading} from "../../misc/loading.decorator";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  templateUrl: "./select-target-customer.template.pug",
  providers: [
    ProjectService,
    CustomerService,
    ConfirmDialogService,
  ],
})

export class SelectTargetCustomerComponent extends WorkflowStep {
  project: any = {};
  customer: any = {};
  customers: CustomerModel[] = [];
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  searchText: string = "";
  error: Error;
  loading: boolean = true;

  checkStepIsComplete() {
    if (this.project.k2CustomerName !== undefined || this.project.k2CustomerName !== null) {
      this.workflowService.setStepStatus(WorkflowStepStatus.Completed);
    }
  }

  @handleErrors()
  async loadCustomers() {
    const {rows, count} = await this.customerService.list(
      +(this.currentPage) - 1,
      +(this.pageSize),
      this.searchText,
    );

    this.customers = rows;
    this.maxPages = Math.ceil(count / (+this.pageSize));
    this.totalCount = count;
  }

  @toggleLoading()
  @handleErrors()
  async ready(workflowTargetInfo: WorkflowTargetInfoModel) {
    this.project = await this.projectService.getProjectById(workflowTargetInfo.targetId).toPromise();

    await this.loadCustomers();

    this.checkStepIsComplete();
  }

  @handleErrors()
  async setCustomer(customer): Promise<boolean> {
    await this.projectService.saveCustomer(this.project.id, customer.id, customer.name).toPromise();

    this.project.k2Customer = customer.id;
    this.project.k2CustomerName = customer.name;
    await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);

    return true;
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    this.loadCustomers();
  }

  async clearSelection(): Promise<boolean> {
    const doRemove = await this.confirmDialogService.confirm(
      "Confirm Clear Customer",
      "Are you sure you want to clear the selected customer? You won't be able to continue with the project and deploy devices.",
    );

    if (!doRemove) return false;

    const result = await this.projectService.unassignCustomer(this.project.id).toPromise();

    this.project.k2Customer = result.k2Customer;
    this.project.k2CustomerName = result.k2CustomerName;

    return true;
  }

  async editCustomHeaders() {
    this.loading = true;

    await CustomerCustomHeadersDialogComponent.showCreateDialog(this.dialogService, {
      k2Customer: this.project.k2Customer,
      k2CustomerName: this.project.k2CustomerName,
    });

    this.loading = false;
  }

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    public projectService: ProjectService,
    public customerService: CustomerService,
    public dialogService: DialogService,
    private confirmDialogService: ConfirmDialogService,
  ) {
    super();
  }
}
